import { useState, useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useStoreModelStores,
  useOverrideAllocations,
  useResetAllocations,
} from '@/hooks/useStoreModels'
import { queryKeys } from '@/lib/queryKeys'
import { MembersFilterBar } from './MembersFilterBar'
import { MembersGrid } from './MembersGrid'
import { RecalculationSection } from './RecalculationSection'
import { Pagination } from '@/components/common/Pagination'
import { toast } from '@/stores/useToastStore'
import type { StoreModelDetailRead } from '@/types'
import type { AxiosError } from 'axios'

interface StoreModelMembersTabProps {
  model: StoreModelDetailRead
}

export function StoreModelMembersTab({ model }: StoreModelMembersTabProps) {
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('+store_number')
  const [storeGroupId, setStoreGroupId] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Convert AG-Grid sort model to API format
  const handleSortChange = useCallback(
    (sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>) => {
      if (sortModel.length === 0) {
        return // No sorting
      }
      const { colId, sort: direction } = sortModel[0]
      const prefix = direction === 'asc' ? '+' : '-'
      setSort(`${prefix}${colId}`)
    },
    []
  )

  // Reset page when filters change
  const prevFiltersRef = useRef({ search, sort, storeGroupId })
  useEffect(() => {
    const prev = prevFiltersRef.current
    if (
      prev.search !== search ||
      prev.sort !== sort ||
      prev.storeGroupId !== storeGroupId
    ) {
      setPage(1)
      prevFiltersRef.current = { search, sort, storeGroupId }
    }
  }, [search, sort, storeGroupId])

  const { data, isLoading, isFetching } = useStoreModelStores({
    modelId: model.id,
    page,
    pageSize,
    search,
    sort,
    storeGroupId,
  })

  const overrideMutation = useOverrideAllocations(model.id)
  const resetMutation = useResetAllocations(model.id)

  const members = data?.data ?? []
  const totalPages = data ? Math.ceil(data.pagination.total / pageSize) : 0

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.storeModels.stores(model.id, {}),
      exact: false,
    })
  }, [queryClient, model.id])

  const handleRecalculationComplete = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.storeModels.stores(model.id, {}),
      exact: false,
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.storeModels.detail(model.id),
    })
  }, [queryClient, model.id])

  const handleOverride = useCallback(
    (
      storeId: string,
      field: 'total_sales' | 'total_units',
      value: number | null
    ) => {
      overrideMutation.mutate(
        {
          overrides: [
            {
              store_id: storeId,
              total_sales: field === 'total_sales' ? value : null,
              total_units: field === 'total_units' ? value : null,
            },
          ],
        },
        {
          onError: (error: AxiosError) => {
            const detail = (
              error.response?.data as { detail?: string } | undefined
            )?.detail
            toast.error(detail ?? 'Failed to override allocation')
          },
        }
      )
    },
    [overrideMutation]
  )

  const handleResetOverrides = useCallback(() => {
    resetMutation.mutate(undefined, {
      onError: (error: AxiosError) => {
        const detail = (error.response?.data as { detail?: string } | undefined)
          ?.detail
        toast.error(detail ?? 'Failed to reset overrides')
      },
    })
  }, [resetMutation])

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }, [])

  return (
    <div className="flex flex-col gap-4">
      {/* Recalculation — DYNAMIC models only */}
      {model.model_type === 'DYNAMIC' && (
        <RecalculationSection
          modelId={model.id}
          onRecalculationComplete={handleRecalculationComplete}
        />
      )}

      {/* Filter bar */}
      <MembersFilterBar
        modelId={model.id}
        search={search}
        onSearchChange={setSearch}
        sort={sort}
        onSortChange={setSort}
        storeGroupId={storeGroupId}
        onStoreGroupIdChange={setStoreGroupId}
        onRefresh={handleRefresh}
        isRefreshing={isFetching}
        onResetOverrides={handleResetOverrides}
        isResetting={resetMutation.isPending}
      />

      {/* Members grid */}
      <MembersGrid
        members={members}
        onOverride={handleOverride}
        loading={isLoading}
        onSortChange={handleSortChange}
      />

      {/* Pagination */}
      {totalPages > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  )
}
